/**
 * Post sign-in return path. Every sign-in entry point (top nav, the city's
 * "sign in required" prompt, paywalls, ProtectedRoute) passes the page the user
 * started on, and every sign-in completion (password, register, OAuth and magic
 * link via /auth/verify) sends them back there. /me is only the fallback when
 * there is no origin page.
 *
 * OAuth and magic links are full-page round trips through Atlas, so router
 * state and the /login query string are gone by the time /auth/verify loads.
 * The path is therefore also stashed in localStorage (with a short TTL) before
 * the user leaves, and taken (single-use) when the session lands. localStorage
 * rather than sessionStorage so a magic link opened in a new tab still works.
 *
 * Only same-origin relative paths are ever accepted, to avoid an open redirect.
 */

export const DEFAULT_RETURN_PATH = '/me'
const KEY = 'gw.returnTo'
const TTL_MS = 60 * 60 * 1000 // 1 hour: long enough for an OAuth hop or a magic-link email
const PARAM_NAMES = ['return', 'returnTo', 'returnUrl', 'next', 'redirect'] as const

// Auth pages (optionally locale-prefixed) are never valid return targets: they
// would bounce the user straight back into the sign-in flow.
const AUTH_PATH_RE = /^\/(?:[a-z]{2}(?:-[A-Za-z0-9]{2,8})?\/)?(?:login|register|auth\/verify|reset-password|account\/verify)(?:[/?#]|$)/

/**
 * Return a safe same-origin path (pathname + search + hash) or null.
 * Rejects absolute URLs, protocol-relative `//host`, backslash tricks, control
 * characters, and auth pages.
 */
export function sanitizeReturnPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const value = raw.trim()
  if (!value || value.length > 2048) return null
  if (!value.startsWith('/')) return null
  if (value.startsWith('//') || value.startsWith('/\\')) return null
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(value)) return null
  let url: URL
  try {
    const probe = 'https://return.invalid'
    url = new URL(value, probe)
    if (url.origin !== probe) return null
  } catch {
    return null
  }
  const path = `${url.pathname}${url.search}${url.hash}`
  if (AUTH_PATH_RE.test(url.pathname)) return null
  return path
}

/** The current page as a return path (pathname + search + hash). */
export function currentReturnPath(loc: { pathname: string; search?: string; hash?: string } = window.location): string {
  return `${loc.pathname}${loc.search ?? ''}${loc.hash ?? ''}`
}

/** `/login` (or another auth page) carrying the return path as `?return=`. */
export function authHrefWithReturn(authPath: string, returnPath: string | null | undefined): string {
  const safe = sanitizeReturnPath(returnPath)
  return safe ? `${authPath}?return=${encodeURIComponent(safe)}` : authPath
}

/**
 * Read the requested return path from router state (`{ from }`) or the query
 * string (`?return=`, `?returnTo=`, `?returnUrl=`, `?next=`, `?redirect=`).
 */
export function readRequestedReturnPath(state: unknown, search: string): string | null {
  const from = state && typeof state === 'object' ? (state as { from?: unknown }).from : undefined
  const fromState = sanitizeReturnPath(from)
  if (fromState) return fromState
  const params = new URLSearchParams(search)
  for (const name of PARAM_NAMES) {
    const safe = sanitizeReturnPath(params.get(name))
    if (safe) return safe
  }
  return null
}

export function stashReturnPath(path: string | null | undefined): void {
  const safe = sanitizeReturnPath(path)
  try {
    if (safe) localStorage.setItem(KEY, JSON.stringify({ path: safe, at: Date.now() }))
  } catch {
    /* storage disabled: non-fatal */
  }
}

/** Read without clearing (used to keep the target when nothing was requested). */
export function peekReturnPath(now: number = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { path?: unknown; at?: unknown }
    if (typeof parsed.at !== 'number' || now - parsed.at > TTL_MS) {
      localStorage.removeItem(KEY)
      return null
    }
    return sanitizeReturnPath(parsed.path)
  } catch {
    return null
  }
}

/** Read and clear the stashed return path (single-use). */
export function takeReturnPath(now: number = Date.now()): string | null {
  const v = peekReturnPath(now)
  clearReturnPath()
  return v
}

export function clearReturnPath(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
}
